import React, { useMemo } from 'react';
import { FlatList } from "react-native"
import type { BestAvailableOrderProps, MarketProps, OrderProps } from '../../types';
import OrderGradeBar from './OrderGradeBar';
import { Icons } from '../../Components';
import { MarketComponentHelpers } from '../api';
import moment from 'moment-mini';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type OrderBookCardProps = {
    player_id?:string,
    orders: OrderProps[],
    market: MarketProps,
    var_1: number,
    show_grades?:boolean,
    show_orders?:boolean,
    onBuy: (order:OrderProps) => void,
    router_orders?: BestAvailableOrderProps[],
    contest_type?: string | number,
    contest_id?: string | number
}

const OrderBookCard = ({ player_id, orders, market, var_1, show_orders, show_grades, onBuy, router_orders, contest_type, contest_id }:OrderBookCardProps) => {
    const Colors = useColors();
    orders.map(o => console.log(`order ${o.side} ${o.var_1}`))
    // Merge router_orders into orders
    const mergedOrders = useMemo(() => {
        if (!router_orders || router_orders.length === 0) {
            return orders;
        }

        // Filter router_orders by contest_type, contest_id, and market_id
        const filteredRouterOrders = router_orders.filter(order => {
            const matchesType = order.event_type == contest_type;
            const matchesId = order.event_id == contest_id;
            const matchesMarket = order.market_id == market.market_id;
            return matchesType && matchesId && matchesMarket;
        });
        // Convert BestAvailableOrderProps to OrderProps
        const convertedRouterOrders: OrderProps[] = filteredRouterOrders.map(routerOrder => ({
            ...routerOrder,
            order_type: 'limit',
            collar_pct: 0,
            be_type: 'market',
            position_ids: [],
            h2h: false,
            market_type:'FOR_MONEY',
            positions: [],
            available_for_parlays: false,
            status: 'approved',
            resolution_status: 'inprogress',
            commission_pct: 0
        }));

        // Merge with existing orders
        return [...orders, ...convertedRouterOrders];
    }, [orders, router_orders, contest_type, contest_id, market.market_id]);

    //mergedOrders.map(o => console.log(`${o.var_1} ${o.side}`))
    // Filter trade side orders to exact var_1
    const trade_side_orders = mergedOrders
        .filter(o => o.side == market.trade_side && o.var_1 == var_1)
        .sort((a,b) => a.probability - b.probability)
        .splice(0, 9);

    // Filter opposing side orders with spread flip logic
    const o_side_orders = mergedOrders
        .filter(o => {
            if (o.side === market.trade_side) return false;
            // For spreads, flip the var_1 sign for opposing side
            if (market.type === 'Spread') {
                return o.var_1 == (var_1 * -1);
            }
            // For non-spreads (totals, etc), use same var_1
            return o.var_1 == var_1;
        })
        .sort((a,b) => a.probability - b.probability)
        .splice(0, 9)

    const handleOrderPress = async(order:OrderProps) => {
        return onBuy({
            ...order,
            buy_sell_ind: 'buy',
            h2h:false,
            h2h_id:undefined,
            draft_liquidity: order.open_amt,
            open_amt: 10,
            fund_id:undefined,
            expire_datetime: moment().add(1, 'minute')
        })
    }

    const renderTradeSide = (data: {item: OrderProps, index:number}) => {
        const cl = data.item.market_type === 'FOR_MONEY' ? '$' : 'E'
        const mine = data.item.player_id == player_id ? true : false
        return (
            <Button float style={{ minHeight:40, padding:8, backgroundColor:show_orders||mine?Colors.order_book.selected:Colors.views.header, margin:4, marginLeft:8, borderRadius:4 }} onPress={() => handleOrderPress(data.item)}>
                {show_orders || mine ?
                <View transparent style={{ flexGrow:1, flexDirection:'row', alignItems:'center' }}>
                    <OrderGradeBar mask={show_grades?false:true} grade={data.item.grade} view_type='book' />
                    <Text size={12} theme='h2' textAlign='center' style={{flex:1/2}}>{MarketComponentHelpers.getOddsLabel(data.item.odds)}</Text>
                    <Text size={12} theme='h2' textAlign='center' style={{flex:1/2}}>{cl}{data.item.open_amt.toFixed(2)}</Text>
                </View>
                :
                <View transparent style={{ flex:1, justifyContent:'center', alignItems:'center' }}>
                    <Icons.LockClosedIcon color={Colors.text.gold} size={14}/>
                </View>
                }
                {mine ?
                <View style={{ position:'absolute', right:0, bottom:0, top:0, width:5, backgroundColor:Colors.text.action, borderRadius:4 }} />
                :<></>}
            </Button>
        )
    }
    const renderOSide = (data: {item: OrderProps, index:number}) => {
        const cl = data.item.market_type === 'FOR_MONEY' ? '$' : 'E'
        const mine = data.item.player_id == player_id ? true : false
        return (
            <Button float style={{ minHeight:40, padding:8, backgroundColor:show_orders||mine?Colors.order_book.selected:Colors.views.header, margin:4, marginLeft:8, borderRadius:4 }} onPress={() => handleOrderPress(data.item)}>
                {show_orders || mine ?
                <View transparent style={{ flexGrow:1, flexDirection:'row', alignItems:'center' }}>
                    <Text size={12} theme='h2' textAlign='center' style={{flex:1}}>{cl}{data.item.open_amt.toFixed(2)}</Text>
                    <Text size={12} theme='h2' textAlign='center' style={{flex:1}}>{MarketComponentHelpers.getOddsLabel(data.item.odds)}</Text>
                    <OrderGradeBar  mask={show_grades?false:true} grade={data.item.grade} view_type='book' />
                </View>
                :
                <View transparent style={{ flex:1, justifyContent:'center', alignItems:'center' }}>
                    <Icons.LockClosedIcon color={Colors.text.gold} size={14}/>
                </View>
                }
                {mine ?
                <View style={{ position:'absolute', left:0, bottom:0, top:0, width:5, backgroundColor:Colors.text.action, borderRadius:4 }} />
                :<></>}
            </Button>
        )
    }

    return (
        <View>
            <View style={{ flexGrow:1, flexDirection:'row' }}>
                <View nativeID="other_side_orders" style={{ flex:1 }}>
                    <FlatList data={o_side_orders} renderItem={renderOSide} keyExtractor={(item) => item.order_id.toString()}/>
                </View>
                <View nativeID="trade_side_orders" style={{ flex:1 }}>
                    <FlatList data={trade_side_orders} renderItem={renderTradeSide} keyExtractor={(item) => item.order_id.toString()}/>
                </View>
            </View>
            <View nativeID='legend' style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', padding:10 }}>
                <View style={{ height:10, width:10, borderRadius:4, marginRight:5, backgroundColor:Colors.text.action }} />
                <Text size={12} theme='description'>Your order</Text>
            </View>
        </View>
        
    )
}

export default OrderBookCard